import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { RollingNumber } from './RollingNumber'
import { POP_FLIGHT_MS } from '../ScorePops'

interface ScorePillProps {
  /** Points hanging on the letters right now: 1 a ring, 10 for the star ring. */
  score: number
  /** The most this visitor has had hanging at once. */
  best: number
  /** The visitor has caught the star ring, this visit or an earlier one. */
  star: boolean
  /** Bumped by every catch of the star ring: each one lights the scoreboard up as its star lands. */
  starCatches: number
}

/**
 * The scoreboard, bottom centre over the pile: how many rings hang on the letters now, big, and the visitor's
 * best under it in the brand's yellow, which brightens for a moment when it is beaten. Shown
 * from the first toss on, zero included, so the game is understood to be scored.
 *
 * Each change is felt as well as read: the digits roll the way the score went, a gain makes the
 * pill jump, a new best makes it jump higher and glow, and a loss only lets it sag a little.
 */
export const ScorePill: React.FC<ScorePillProps> = ({ score, best, star, starCatches }) => {
  const [beaten, setBeaten] = useState(false)
  /** Bumped by every new best, so a second best within the glow restarts it. */
  const [bestBeats, setBestBeats] = useState(0)
  const prevBest = useRef(best)
  const prevScore = useRef(score)
  const pillRef = useRef<HTMLDivElement>(null)
  const glowRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!bestBeats) return
    setBeaten(true)
    const t = setTimeout(() => setBeaten(false), 1800)
    return () => clearTimeout(t)
  }, [bestBeats])

  // The star ring caught: as its star flies in, the whole glow lights up and the board jumps.
  useLayoutEffect(() => {
    if (!starCatches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const landing = { delay: POP_FLIGHT_MS * 0.95 }
    glowRef.current?.animate([{ opacity: 0.25 }, { opacity: 1, offset: 0.15 }, { opacity: 0.25 }], {
      ...landing,
      duration: 2200,
      easing: 'ease-out',
    })
    pillRef.current?.animate(
      [{ transform: 'none' }, { transform: 'scale(1.25)', offset: 0.3 }, { transform: 'none' }],
      {
        ...landing,
        duration: 700,
        easing: 'cubic-bezier(0.3, 1.6, 0.5, 1)',
      }
    )
  }, [starCatches])

  useLayoutEffect(() => {
    const pill = pillRef.current
    const gained = score > prevScore.current
    const lost = score < prevScore.current
    const newBest = best > prevBest.current
    prevScore.current = score
    prevBest.current = best
    if (newBest) setBestBeats((n) => n + 1)
    if (!pill || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // The glow behind the glass flares with a gain, as the links' glow does on hover.
    if (gained || newBest) {
      glowRef.current?.animate([{ opacity: 0.25 }, { opacity: newBest ? 0.9 : 0.6, offset: 0.25 }, { opacity: 0.25 }], {
        duration: newBest ? 1400 : 900,
        easing: 'ease-out',
      })
    }
    if (newBest) {
      pill.animate([{ transform: 'none' }, { transform: 'scale(1.15)', offset: 0.35 }, { transform: 'none' }], {
        duration: 520,
        easing: 'cubic-bezier(0.3, 1.6, 0.5, 1)',
      })
    } else if (gained) {
      pill.animate([{ transform: 'none' }, { transform: 'scale(1.08)', offset: 0.35 }, { transform: 'none' }], {
        duration: 300,
        easing: 'ease-out',
      })
    } else if (lost) {
      pill.animate([{ transform: 'none' }, { transform: 'translateY(3px)', offset: 0.4 }, { transform: 'none' }], {
        duration: 360,
        easing: 'ease-in-out',
      })
    }
  }, [score, best])

  const label = score === 1 ? '1 point on the letters' : `${score} points on the letters`
  // What counts, for whoever hovers or focuses the pill.
  const explain = `A ring counts when it hangs on a letter once the water settles: hooked on a tip, hanging from an arm or resting on a crossbar. Rings piled on top of those do not count, and every jet shakes a heap down. Each ring is worth 1; the silver star ring, very hard to hook, is worth 10. Best: the most you have had hanging at once on this device.`
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[25] flex justify-center animate-rise motion-reduce:animate-none md:bottom-[max(1.5rem,env(safe-area-inset-bottom))] short:hidden">
      <div
        ref={pillRef}
        data-testid="score"
        title={explain}
        tabIndex={0}
        className="pointer-events-auto relative flex cursor-help flex-col items-center rounded-2xl px-4 pt-1.5 pb-2 outline-none focus-visible:ring-2 focus-visible:ring-white/70"
      >
        {/* The links' glass: a glow in the rings' colours behind, frosted glass over it. */}
        <span
          ref={glowRef}
          aria-hidden="true"
          className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#ff2bff] via-[#2bf5ff] to-[#ffef3a] opacity-25 blur-md"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-2xl border border-white/10 bg-[hsla(0,0%,100%,0.06)] backdrop-blur-md"
        />
        {/* Said once, when it has changed; the rolling digits are for the eye. */}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {label}
          {best > 0 && `, best ${best}`}
          {star && ', star ring caught'}
        </span>
        <span
          aria-hidden="true"
          className="relative text-3xl leading-none font-bold text-white tabular-nums [filter:drop-shadow(0_0_10px_rgb(255_255_255/0.35))] md:text-4xl"
        >
          <RollingNumber value={score} />
        </span>
        <span
          aria-hidden="true"
          className="relative mt-1 text-[10px] uppercase tracking-[0.22em] whitespace-nowrap text-white/75 tabular-nums md:text-[11px] md:tracking-[0.25em]"
        >
          on the letters
          {best > 0 && (
            <>
              <span className="text-white/30"> · </span>
              <span
                className={`transition-[color,filter] duration-500 ${
                  beaten ? 'text-[#ffef3a] [filter:drop-shadow(0_0_6px_rgb(255_239_58/0.8))]' : 'text-[#ffef3a]/75'
                }`}
              >
                best <RollingNumber value={best} />
              </span>
            </>
          )}
          {star && (
            <span className="star-shine" title="The star ring: you hooked it">
              {' '}
              ★
            </span>
          )}
        </span>
      </div>
    </div>
  )
}
